/**
 * First Delivery Date modal + default-offset cart stamper.
 *
 * Two responsibilities:
 *
 * 1. Quiz-only modal flow. Exposes a public Promise API at
 *    `window.FuzzballFirstDelivery.askForDate()` which the quiz
 *    controller's handleCheckout calls at the very start — before any
 *    cart mutation or state write. Returns Promise<boolean>: true if
 *    the customer confirmed (cart attribute already written), false if
 *    they dismissed. Dismissing leaves cart and quiz state untouched.
 *
 * 2. Default-offset stamper. On init, writes the schema-configured
 *    default offset to cart.attributes so the Vercel webhook can fall
 *    back to it when a customer didn't pick a date (D4.12). Idempotent —
 *    skips the write if the current cart attribute already matches.
 *
 * Phase 2 (Vercel webhook) reads both attributes from the order's
 * order_attributes and applies the resolved date to the Recharge
 * subscription via POST /subscriptions/{id}/charge_date.
 */
(function () {
  'use strict';

  const ATTR_DATE = '_first_delivery_date';
  const ATTR_DEFAULT_OFFSET = '_first_delivery_default_offset_days';
  const ATTR_MIN_DAYS = '_first_delivery_min_days';

  let modal = null;
  let overlay = null;
  let pickerInstance = null;
  let lastFocused = null;
  let pendingResolve = null; // Promise<boolean> resolver for askForDate()

  function $(sel, root) { return (root || document).querySelector(sel); }

  function todayMidnight() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function toISODate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function clamp(n, lo, hi) {
    return Math.min(Math.max(n, lo), hi);
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  async function writeCartAttribute(date) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [ATTR_DATE]: date } })
    });
    if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);
    return res.json();
  }

  // D4.12: stamp the schema-configured default offset AND min-days onto the
  // cart so the webhook can (a) fall back to default-offset for customers
  // who didn't pick a date, and (b) validate that a modal-picked date hasn't
  // decayed below the operational floor between pick-time and order-time
  // (abandoned-cart scenario). Runs regardless of the calendar enable
  // toggle — when the toggle is off, this is the only thing telling the
  // webhook what defaults to apply (otherwise post-823476 retirement the
  // order would get no override at all). Idempotent: writes only the
  // attributes whose current cart value differs from the schema value.
  async function ensureDefaultsOnCart() {
    const offset = readGlobalInt('FUZZBALL_FIRST_DELIVERY_DEFAULT_OFFSET', modal && modal.dataset.fddDefaultOffset);
    const minDays = readGlobalInt('FUZZBALL_FIRST_DELIVERY_MIN_DAYS', modal && modal.dataset.fddMinDays);
    const targets = {};
    if (Number.isFinite(offset) && offset > 0) targets[ATTR_DEFAULT_OFFSET] = String(offset);
    if (Number.isFinite(minDays) && minDays > 0) targets[ATTR_MIN_DAYS] = String(minDays);
    if (!Object.keys(targets).length) return;

    try {
      const cart = await fetchCart();
      const current = (cart && cart.attributes) || {};
      const toWrite = {};
      for (const key of Object.keys(targets)) {
        if (current[key] !== targets[key]) toWrite[key] = targets[key];
      }
      if (!Object.keys(toWrite).length) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: toWrite })
      });
    } catch (err) {
      console.warn('[first-delivery-modal] defaults stamp failed:', err);
    }
  }

  function readGlobalInt(globalName, datasetFallback) {
    const fromGlobal = parseInt(window[globalName], 10);
    if (Number.isFinite(fromGlobal)) return fromGlobal;
    const fromDataset = parseInt(datasetFallback, 10);
    return Number.isFinite(fromDataset) ? fromDataset : NaN;
  }

  function showError(msg) {
    const el = $('[data-fdd-error]', modal);
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function clearError() {
    const el = $('[data-fdd-error]', modal);
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  function setBusy(busy) {
    const confirmBtn = $('[data-fdd-confirm]', modal);
    if (!confirmBtn) return;
    confirmBtn.disabled = busy;
    confirmBtn.setAttribute('aria-busy', String(busy));
  }

  function isOpen() {
    return modal && modal.style.display !== 'none' && !modal.hidden;
  }

  async function openModal() {
    if (!modal) return;
    lastFocused = document.activeElement;
    clearError();
    setBusy(false);

    const minDays = parseInt(modal.dataset.fddMinDays, 10) || 7;
    const maxDays = parseInt(modal.dataset.fddMaxDays, 10) || 28;
    const defaultOffset = parseInt(modal.dataset.fddDefaultOffset, 10) || 12;

    const today = todayMidnight();
    const minDate = addDays(today, minDays);
    const maxDate = addDays(today, maxDays);
    const clampedOffset = clamp(defaultOffset, minDays, maxDays);
    let defaultDate = addDays(today, clampedOffset);

    // If a previous pick lives on the cart and is still in the future, use it.
    // If it's stale (in the past), eagerly overwrite the cart attribute with
    // the default offset so a stale value can never ride into the order.
    try {
      const cart = await fetchCart();
      const stored = cart && cart.attributes && cart.attributes[ATTR_DATE];
      if (stored) {
        const storedDate = new Date(stored + 'T00:00:00');
        if (!isNaN(storedDate) && storedDate >= today) {
          defaultDate = storedDate < minDate ? minDate
            : storedDate > maxDate ? maxDate
            : storedDate;
        } else {
          await writeCartAttribute(toISODate(defaultDate)).catch(() => {});
        }
      }
    } catch (err) {
      console.warn('[first-delivery-modal] cart read failed:', err);
    }

    // Match the .extras-popup-system show pattern: display:block first,
    // then add the active class on the next tick to trigger the animation.
    modal.style.display = 'block';
    modal.removeAttribute('hidden');
    if (overlay) overlay.classList.add('active');
    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      if (modal) modal.classList.add('extras-popup-active');
    }, 10);

    const input = $('[data-fdd-picker]', modal);
    const sheet = $('.first-delivery-modal__sheet', modal);

    if (typeof flatpickr === 'function' && input) {
      pickerInstance = flatpickr(input, {
        minDate,
        maxDate,
        defaultDate,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'l, F j, Y',
        // Apply our class to the visible alt-input so it picks up the full
        // width / centered styling — without this, only the hidden source
        // input has the class and the visible field falls back to defaults.
        altInputClass: 'first-delivery-modal__input first-delivery-modal__input--alt',
        disableMobile: false,
        static: true,
        monthSelectorType: 'static'
      });
    } else if (input) {
      // Graceful degradation: native date input
      input.type = 'date';
      input.removeAttribute('readonly');
      input.min = toISODate(minDate);
      input.max = toISODate(maxDate);
      input.value = toISODate(defaultDate);
    }

    if (typeof window.trapFocus === 'function' && sheet) {
      window.trapFocus(sheet, sheet);
    } else if (sheet) {
      sheet.focus();
    }
  }

  function closeModal(confirmed) {
    if (!modal || !isOpen()) return;

    modal.classList.remove('extras-popup-active');
    if (overlay) overlay.classList.remove('active');

    if (pickerInstance && typeof pickerInstance.destroy === 'function') {
      pickerInstance.destroy();
      pickerInstance = null;
    }

    document.body.classList.remove('overflow-hidden');

    // Hide after slide-down animation completes (matches .extras-popup-system)
    setTimeout(() => {
      if (modal) modal.style.display = 'none';
    }, 400);

    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(lastFocused);
    } else if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;

    // Resolve any pending askForDate() Promise
    if (pendingResolve) {
      const r = pendingResolve;
      pendingResolve = null;
      r(!!confirmed);
    }
  }

  function readPickedDate() {
    const input = $('[data-fdd-picker]', modal);
    if (!input) return null;
    if (pickerInstance && pickerInstance.input) {
      return pickerInstance.input.value || null;
    }
    return input.value || null;
  }

  async function onConfirm() {
    clearError();
    const date = readPickedDate();
    if (!date) {
      showError('Please pick a date.');
      return;
    }

    setBusy(true);
    try {
      await writeCartAttribute(date);
    } catch (err) {
      console.error('[first-delivery-modal] cart attribute write failed:', err);
      showError('Could not save your date. Please try again.');
      setBusy(false);
      return;
    }

    // Tell the quiz controller to refresh its plan-summary date label
    if (window.fuzzballQuiz) {
      window.fuzzballQuiz._cachedFirstDeliveryDate = date;
    }

    closeModal(true);
  }

  // Public API: open the modal and resolve with true (confirmed) or
  // false (dismissed via close X / overlay / Esc). Re-entrant safe.
  function askForDate() {
    if (!modal) return Promise.resolve(true); // feature off — let caller proceed
    if (pendingResolve) {
      // Re-entrant call: cancel the previous, start fresh
      const r = pendingResolve;
      pendingResolve = null;
      r(false);
      closeModal(false);
    }
    return new Promise((resolve) => {
      pendingResolve = resolve;
      openModal();
    });
  }

  function onModalClick(e) {
    if (e.target.closest('[data-fdd-close]') || e.target.closest('[data-fdd-overlay]')) {
      closeModal(false);
      return;
    }
    if (e.target.closest('[data-fdd-confirm]')) {
      onConfirm();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen()) {
      closeModal(false);
    }
  }

  function init() {
    modal = $('[data-fdd-modal]');
    overlay = $('[data-fdd-overlay]');

    // Always expose the public API — even if the modal isn't rendered.
    // When modal is null, askForDate() resolves true so the quiz checkout
    // flow proceeds normally (matches the disabled-feature contract).
    window.FuzzballFirstDelivery = { askForDate };

    // Always stamp the schema defaults (default-offset + min-days) onto the
    // cart, regardless of whether the modal UI is rendered. These let the
    // webhook (a) fall back to the configured offset when the customer
    // didn't pick a date, and (b) reject stale picks that decayed below the
    // operational floor between pick-time and order-time.
    ensureDefaultsOnCart();

    if (!modal) return; // setting disabled / snippet absent — modal UI only

    modal.addEventListener('click', onModalClick);
    if (overlay) overlay.addEventListener('click', () => closeModal(false));
    document.addEventListener('keydown', onKeydown);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
